import {
  CANVAS_ELEMENT_TYPES,
  CANVAS_TOOLS,
  createCanvasElementId,
  createImageElement,
  fitImageElementSize,
} from "../nativeCanvasElements";
import {
  getStageCenterScreenPoint,
  screenToScene,
} from "../geometry/viewportTransform";

/**
 * Image mutations on the engine scene. `addImage` is a normal committed
 * (undoable) insert; `replaceImageSource` is deliberately history-transparent
 * — see below.
 * @param {{ commitElements: Function, defaultTool?: string, markElementRevision?: Function, notifyScene: Function, requestRender: Function, setTool: Function, state: object }} internals
 * @returns {{ addImage: (src: string) => string, replaceImageSource: (params: { elementId: string, src: string }) => boolean }}
 */
export const createImageActions = ({
  commitElements,
  defaultTool = CANVAS_TOOLS.SELECT,
  markElementRevision,
  notifyScene,
  requestRender,
  setTool,
  state,
}) => {
  const addImage = (src, naturalSize) => {
    const centerPoint = screenToScene({
      point: getStageCenterScreenPoint(state.size),
      viewport: state.viewport,
    });
    const size = fitImageElementSize({
      naturalSize,
      zoom: state.viewport.zoom,
    });
    const element = createImageElement({
      id: createCanvasElementId(),
      point: {
        x: centerPoint.x - size.width / 2,
        y: centerPoint.y - size.height / 2,
      },
      size,
      src,
    });
    commitElements([...state.elements, element], {
      selectIds: new Set([element.id]),
    });
    setTool(defaultTool);
    return element.id;
  };

  const replaceImageSource = ({ elementId, src }) => {
    const element = state.elements.find(
      (candidate) => candidate.id === elementId
    );
    if (!element || element.type !== CANVAS_ELEMENT_TYPES.IMAGE) return false;
    if (!src || element.src === src) return false;

    // In-place mutation, breaking the usual immutable-commit rule on purpose:
    // history entries share this element object, so an upload swap applies
    // across the whole undo/redo timeline and never resurrects base64 pixels
    // — and undo/redo stacks stay exactly as the user left them.
    element.src = src;
    markElementRevision?.();
    notifyScene();
    requestRender("static");
    return true;
  };

  return { addImage, replaceImageSource };
};
